/**
 * Geofence Service
 * Handles location checking against backend geofences
 * Makes location compulsory for Android devices
 */

import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { getStorageJSON, setStorageJSON } from '../utils/capacitorStorage';
import api from './api';

class GeofenceService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    // Kept as alias for backwards compatibility across existing code
    this.isAndroid = this.isNative;
    this.locationPermissionGranted = false;
    this.lastLocation = null;
    this.lastCheckTime = null;
    this.lastResult = null;
    this.cacheDuration = 300000; // Cache location check for 5 minutes
    // Strict mode: when enabled, require location + geofence compliance (block usage if outside).
    // We enable this for native operator apps (iOS & Android), not for the web admin portal.
    this.strictMode = false;
  }

  /**
   * Enable/disable strict geofence enforcement.
   * When enabled, location is mandatory on all platforms.
   */
  setStrictMode(enabled) {
    this.strictMode = Boolean(enabled);
  }

  /**
   * Request location permissions (required for Android)
   */
  async requestPermissions() {
    if (!this.isAndroid) {
      // Web/other platforms - permissions handled by browser
      return { granted: true };
    }

    try {
      const permission = await Geolocation.requestPermissions();
      const granted = permission.location === 'granted';
      this.locationPermissionGranted = granted;
      return { ...permission, granted };
    } catch (error) {
      console.error('[GeofenceService] Error requesting permissions:', error);
      return { granted: false, error: error.message };
    }
  }

  /**
   * Check if location permissions are granted
   */
  async checkPermissions() {
    if (!this.isAndroid) {
      return { granted: true };
    }

    try {
      const permission = await Geolocation.checkPermissions();
      const granted = permission.location === 'granted';
      this.locationPermissionGranted = granted;
      return { ...permission, granted };
    } catch (error) {
      console.error('[GeofenceService] Error checking permissions:', error);
      return { granted: false };
    }
  }

  /**
   * Get current device location
   */
  async getCurrentLocation() {
    // Check permissions first
    const permission = await this.checkPermissions();
    if (!permission.granted && this.isAndroid) {
      // Request permissions if not granted
      const requested = await this.requestPermissions();
      if (!requested.granted) {
        throw new Error('Location permission is required. Please enable location access in app settings.');
      }
    }

    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0, // Always get fresh location
      });

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      };

      this.lastLocation = location;
      return location;
    } catch (error) {
      console.error('[GeofenceService] Error getting location:', error);
      throw new Error(`Failed to get location: ${error.message}`);
    }
  }

  /**
   * Check if current location is within any active geofence
   * @param {boolean} forceFresh - Force fresh location check (ignore cache)
   * @returns {Promise<{inside: boolean, geofence: object|null, distance_meters: number|null, location: object}>}
   */
  async checkLocationInGeofence(forceFresh = false) {
    if (!forceFresh) {
      const cachedResult = this.getCachedResult();
      if (cachedResult) return cachedResult;

      try {
        const dailyCache = await getStorageJSON('dailyGeofenceResult');
        if (dailyCache && dailyCache.timestamp && dailyCache.result && dailyCache.result.inside) {
          const today = new Date().toLocaleDateString();
          const cacheDate = new Date(dailyCache.timestamp).toLocaleDateString();
          if (today === cacheDate) {
            this.lastResult = dailyCache.result;
            this.lastCheckTime = dailyCache.timestamp;
            return dailyCache.result;
          }
        }
      } catch (e) {
        console.warn('[GeofenceService] Error reading daily cache:', e);
      }
    }

    // For Android, location is compulsory
    if (this.isAndroid) {
      try {
        const location = await this.getCurrentLocation();
        const result = await this.checkLocationAgainstBackend(location.latitude, location.longitude, location);
        this.lastCheckTime = Date.now();
        this.lastResult = result;
        if (result.inside) {
          await setStorageJSON('dailyGeofenceResult', { result, timestamp: this.lastCheckTime });
        }
        return result;
      } catch (error) {
        // Location is compulsory for Android - throw error
        throw new Error(`Location check failed (required for Android): ${error.message}`);
      }
    }

    // For other platforms, strictMode decides whether to block when location check fails
    try {
      const location = await this.getCurrentLocation();
      const result = await this.checkLocationAgainstBackend(location.latitude, location.longitude, location);
      this.lastCheckTime = Date.now();
      this.lastResult = result;
      if (result.inside) {
        await setStorageJSON('dailyGeofenceResult', { result, timestamp: this.lastCheckTime });
      }
      return result;
    } catch (error) {
      console.warn('[GeofenceService] Location check failed (non-Android):', error);
      if (this.strictMode) {
        // In strict mode, location is compulsory for all platforms
        throw new Error(`Location check failed (required): ${error.message}`);
      }
      // Non-strict: return a warning but don't block
      return {
        inside: false,
        geofence: null,
        distance_meters: null,
        location: null,
        warning: error.message,
      };
    }
  }

  /**
   * Check a specific location (lat, lon) against backend geofences
   */
  async checkLocationAgainstBackend(latitude, longitude, locationData = null) {
    try {
      const response = await api.post('/geofences/check_location/', {
        latitude,
        longitude,
      });

      return {
        inside: response.data.inside,
        geofence: response.data.geofence,
        distance_meters: response.data.distance_meters,
        location: locationData || { latitude, longitude },
      };
    } catch (error) {
      console.error('[GeofenceService] Error checking location against backend:', error);
      
      if (error.response) {
        // Server error
        throw new Error(error.response.data?.detail || 'Failed to check location with server');
      } else if (error.request) {
        // Network error
        throw new Error('Network error: Unable to connect to server for location check');
      } else {
        // Other error
        throw new Error(`Location check failed: ${error.message}`);
      }
    }
  }

  /**
   * Validate location before allowing action (e.g., starting extrusion session)
   * For Android, this is compulsory and will throw if location check fails
   */
  async validateLocationForAction(actionName = 'this action') {
    try {
      const result = await this.checkLocationInGeofence();
      
      if (!result.inside && (this.isAndroid || this.strictMode)) {
        throw new Error(
          `You must be within an authorized location to ${actionName}. ` +
          `Current location is outside all geofenced areas.`
        );
      }

      if (!result.inside) {
        console.warn(`[GeofenceService] Location check: Outside geofence for ${actionName}`);
      }

      return result;
    } catch (error) {
      if (this.isAndroid || this.strictMode) {
        // For strict enforcement, re-throw the error to block the action
        throw error;
      } else {
        // For other platforms, log warning but allow
        console.warn(`[GeofenceService] Location validation warning: ${error.message}`);
        return {
          inside: false,
          geofence: null,
          distance_meters: null,
          location: null,
          warning: error.message,
        };
      }
    }
  }

  /**
   * Get cached location (if available and recent)
   */
  getCachedLocation() {
    if (this.lastLocation && this.lastCheckTime) {
      const age = Date.now() - this.lastCheckTime;
      if (age < this.cacheDuration) {
        return this.lastLocation;
      }
    }
    return null;
  }

  getCachedResult() {
    if (this.lastResult && this.lastCheckTime) {
      const age = Date.now() - this.lastCheckTime;
      if (age < this.cacheDuration) {
        return this.lastResult;
      }
    }
    return null;
  }
}

// Export singleton instance
const geofenceService = new GeofenceService();
export default geofenceService;
